/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/<domain>/<domain>.contract.ts` (or the
 * shared bases under `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This
 * file exists so the web SPA can import zod schemas + oRPC contract
 * types from `@event-manager/api-client/contracts/<domain>.contract`
 * without reaching into the server workspace.
 */

// Base contract utilities and common schemas
import * as z from "zod";
import { isValidPhoneE164 } from "../common/phone";

// Re-export oc for convenience
export { oc } from "@orpc/contract";

// Common schemas used across contracts
export const PaginationInputSchema = z.object({
	limit: z.number().int().min(1).max(100).optional().default(20),
	cursor: z.number().int().min(0).optional().default(0),
});

export const IdParamSchema = z.object({
	id: z.coerce.number().int().min(1),
});

export const UuidParamSchema = z.object({
	id: z.string().uuid(),
});

export const TimestampSchema = z.object({
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
});

// Common error definitions
export const commonErrors = {
	NOT_FOUND: {
		message: "Resource not found",
		data: z.object({ id: z.union([z.number(), z.string()]) }),
	},
	UNAUTHORIZED: {
		message: "Authentication required",
	},
	FORBIDDEN: {
		message: "Insufficient permissions",
		data: z.object({ requiredRole: z.string().nullish() }),
	},
	VALIDATION_ERROR: {
		message: "Validation failed",
		data: z.object({
			errors: z.array(
				z.object({
					path: z.string(),
					message: z.string(),
				})
			),
		}),
	},
};

// Phone E164 format schema
export const PhoneE164Schema = z
	.string()
	.regex(/^\+[1-9]\d{1,14}$/, "Invalid E164 phone format")
	.max(16);

/**
 * Strict phone schema for write-path inputs: structural E.164 shape plus full
 * libphonenumber-js validation (country code + national number pattern).
 * Mirrors the server's `StrictPhoneE164InputSchema`. Read/response schemas and
 * lookup path params keep the lenient structural `PhoneE164Schema`.
 */
export const StrictPhoneE164InputSchema = PhoneE164Schema.refine(
	(value) => isValidPhoneE164(value),
	"Invalid phone number"
);

// Date range schema - matches tool input schema format
// Uses start/end (YYYY-MM-DD) and optional weekdays filter
export const DateRangeSchema = z.object({
	start: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
		.max(10),
	end: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
		.max(10)
		.optional(),
	weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
});
export type DateRange = z.infer<typeof DateRangeSchema>;

/**
 * Pre-trimmed string schema for user-facing text inputs.
 * Strips leading/trailing whitespace before validation.
 */
export const TrimmedString = z.string().trim();

/**
 * Bounded record utility - caps the number of keys in a z.record() to prevent
 * memory exhaustion from unbounded key counts.
 */
export function boundedRecord<V extends z.ZodTypeAny>(
	valueSchema: V,
	maxKeys = 1000
) {
	return z
		.record(z.string().max(200), valueSchema)
		.refine((obj) => Object.keys(obj).length <= maxKeys, {
			message: `Record cannot have more than ${maxKeys} keys`,
		});
}
